import { PayloadAction } from '@reduxjs/toolkit';
import config from 'config/config';
import findIndex from 'lodash/findIndex';
import { createSagaAction } from 'saga-toolkit';
import { fetchCartData } from 'store/cartSlice';
import { FetchCartDataPayload } from 'store/cartSlice/types';
import {
  getIdOrderItemForTicket,
  getOrderItemsForBundle,
} from 'store/cartSlice/utils';
import {
  isAddon,
  isBundle,
  isSeat,
  isTicket,
  isTopup,
  SelectedItemType,
} from 'types/SelectedItem';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { getBundleFeePrice, getTicketTypes } from 'utils/bundles';
import {
  CartBundle,
  CartItem,
  CartTicket,
  VoucherApplicableTickets,
  VoucherCodeData,
  VoucherData,
  VoucherInterface,
  VoucherStatus,
} from '../../../../types/CartItem';
import {
  getFeePrice,
  getTaxPrice,
  getTicketPriceByQuantity,
} from '../../../../utils/tickets';
import {
  initialSelectedEvent,
  initialVoucher,
  initialVoucherCodeData,
  initialVoucherData,
} from './initialValues';
import {
  AddAddonsToSelectedItemsPayload,
  AddCalendarTicketToCartPayload,
  AddSeatToCartPayload,
  AddTicketsToCartPayload,
  AddTicketTypeBundlesToCartPayload,
  TicketSalePageEvent,
  TicketSaleState,
} from './types';

const sliceName = 'ticketSale';

export const initialState: TicketSaleState = {
  selectedEvent: initialSelectedEvent,
  selectedItems: [],
  loading: true,
  voucher: initialVoucher as VoucherInterface,
  ticketIdsFromQueryParams: '',
};

// saga
export interface fetchEventWithTicketsPayload {
  meta: {
    arg: {
      eventId: string;
      extraIncludes: string[];
      queryParams: {
        uuid: string;
        tickets: string | null;
        preview: string | null;
      };
    };
  };
}

export interface fetchVoucherDataPayload {
  meta: {
    arg: {
      eventId: string;
      voucherCode: string;
      uuid: string;
    };
  };
}
export const fetchEventWithTickets = createSagaAction(
  `${sliceName}/fetchEventWithTickets`,
);
export const fetchVoucherData = createSagaAction(
  `${sliceName}/fetchVoucherData`,
);

const slice = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    setAllTheSelectedItems(
      state,
      action: PayloadAction<{
        cartItems: CartItem[];
        cartEvent: TicketSalePageEvent;
      }>,
    ) {
      const { cartItems, cartEvent } = action.payload;

      const ticketIDs = cartEvent.tickets.map(ticket => ticket.id);
      const bundleIDs = cartEvent.ticketTypeBundles?.map(bundle => bundle.id);

      state.selectedItems = cartItems.filter(item => {
        if (isBundle(item)) {
          return bundleIDs!.includes(item.id);
        }
        return ticketIDs.includes(item.ticketId);
      });
    },
    setQueryParams(state, action: PayloadAction<string>) {
      state.ticketIdsFromQueryParams = action.payload;
    },
    removeQueryParams(state) {
      state.ticketIdsFromQueryParams = '';
    },
    removeAllTheSelectedItems(state) {
      state.selectedItems = [];
    },
    resetVoucherData(state) {
      state.voucher = initialVoucher as VoucherInterface;
      state.selectedEvent.tickets = state.selectedEvent.tickets.filter(
        ticket => ticket?.visibility !== 'private',
      );
    },
    updateVoucherStatus(
      state,
      action: PayloadAction<{ status: VoucherStatus }>,
    ) {
      state.voucher.status = action.payload.status;
    },
    addTicketsToCart(state, action: PayloadAction<AddTicketsToCartPayload>) {
      const { ticketId, quantity } = action.payload;

      // find selected ticket inside the tickets of the current event
      const itemIndex = findIndex(state.selectedEvent.tickets, {
        id: ticketId,
      });
      const ticketPriceData = getTicketPriceByQuantity(
        state.selectedEvent.tickets[itemIndex].ticketprice,
        quantity,
      );

      // find the ticket inside the selected items
      const index = findIndex(state.selectedItems, {
        type: SelectedItemType.TICKET,
        ticketId,
      });

      // true if ticket was selected before
      if (index !== -1) {
        if (quantity === 0) {
          // remove the selected ticket along with its addons & top-ups
          state.selectedItems = state.selectedItems.filter(item =>
            isBundle(item)
              ? true
              : (isTicket(item) || isAddon(item) || isTopup(item)) &&
                item.ticketId !== ticketId,
          );
        } else {
          const tax = getTaxPrice(
            ticketPriceData?.price,
            state.selectedEvent.tax_rate.rate,
          );
          const fee = getFeePrice(
            ticketPriceData?.price,
            state.selectedEvent.fees,
          );
          const feeTax = getTaxPrice(
            fee,
            state.selectedEvent.fee_tax_rate.rate,
          );
          state.selectedItems[index].quantity = quantity; // update the new quantity
          state.selectedItems[index].price = ticketPriceData?.price; // update the new price
          state.selectedItems[index].tax = tax; // update the new tax
          state.selectedItems[index].fee = fee; // update the new fee
          state.selectedItems[index].feeTax = feeTax; // update the new fee tax
        }
      }

      // check if ticket is discountable
      const ticketIsDiscountable =
        !!state.voucher.voucherApplicableTickets.find(
          ticket => ticket === ticketId,
        );

      let newVoucherStatus = state.voucher.status;
      let newVoucherError = state.voucher.voucherError;

      // calculate the number of voucher uses in the current selected items
      if (ticketIsDiscountable && state.voucher.voucherCode.id) {
        let sum = 0;
        for (let i = 0; i < state.selectedItems.length; i++) {
          const selectedItem = state.selectedItems[i];
          if (
            isTicket(selectedItem) &&
            state.voucher.voucherApplicableTickets.findIndex(
              ticketId => ticketId === selectedItem.ticketId,
            ) >= 0
          ) {
            sum += selectedItem.quantity;
          }
        }

        // if the ticket was not selected before, its quantity ( which equals 1 )
        // should be added to the sum
        if (index === -1) {
          sum += 1;
        }

        const remainingUses =
          state.voucher.voucherCode.uses === 0
            ? config.maximumVoucherCodeUses
            : state.voucher.voucherCode.uses -
              state.voucher.voucherCode.claimed -
              sum;

        // needs to be re-written
        if (remainingUses < 0 && state.voucher.voucherCode.id) {
          newVoucherStatus = 'invalid';
          newVoucherError =
            'This code cannot be applied to the number of tickets you selected.';
        } else if (
          sum <= state.voucher.voucherCode.uses &&
          state.voucher.status === 'invalid'
        ) {
          newVoucherStatus = 'valid';
          newVoucherError = null;
        }
      }

      state.voucher.status = newVoucherStatus;
      state.voucher.voucherError = newVoucherError;

      if (index === -1) {
        const tax = getTaxPrice(
          ticketPriceData?.price,
          state.selectedEvent.tax_rate.rate,
        );
        const fee = getFeePrice(
          ticketPriceData?.price,
          state.selectedEvent.fees,
        );
        const feeTax = getTaxPrice(fee, state.selectedEvent.fee_tax_rate.rate);

        state.selectedItems.push({
          type: SelectedItemType.TICKET,
          ticketId: state.selectedEvent.tickets[itemIndex].id,
          idOrderItem: undefined,
          name: state.selectedEvent.tickets[itemIndex].name,
          quantity: action.payload.quantity,
          price: ticketPriceData?.price,
          tax,
          fee,
          feeTax,
          absorbFees: ticketPriceData.absorb_fees,
        });
      }
    },
    addSeatsToCart(state, action: PayloadAction<AddSeatToCartPayload>) {
      const { ticketId, quantity, seatId, seatLabel } = action.payload;

      if (quantity === 0 && state.selectedItems.length === 0) return;

      // find selected ticket inside the tickets of the current event
      const itemIndex = findIndex(state.selectedEvent.tickets, {
        id: ticketId,
      });
      const ticketPriceData = getTicketPriceByQuantity(
        state.selectedEvent.tickets[itemIndex].ticketprice,
        quantity,
      );

      // find the ticket inside the selected items
      const index = findIndex(state.selectedItems, {
        type: SelectedItemType.TICKET,
        ticketId: ticketId,
        seatId: seatId,
        seatLabel: seatLabel,
      });
      // true if ticket was selected before
      if (index !== -1) {
        if (quantity === 0) {
          // remove the selected seat
          state.selectedItems = state.selectedItems.filter(
            item =>
              isSeat(item) &&
              item.seatId !== seatId &&
              item.seatLabel !== seatLabel,
          );
        } else {
          const tax = getTaxPrice(
            ticketPriceData?.price,
            state.selectedEvent.tax_rate.rate,
          );
          const fee = getFeePrice(
            ticketPriceData?.price,
            state.selectedEvent.fees,
          );
          const feeTax = getTaxPrice(
            fee,
            state.selectedEvent.fee_tax_rate.rate,
          );
          state.selectedItems[index].quantity = quantity; // update the new quantity
          state.selectedItems[index].price = ticketPriceData?.price; // update the new price
          state.selectedItems[index].tax = tax; // update the new tax
          state.selectedItems[index].fee = fee; // update the new fee
          state.selectedItems[index].feeTax = feeTax; // update the new fee tax
        }
      }

      // check if ticket is discountable
      const ticketIsDiscountable =
        !!state.voucher.voucherApplicableTickets.find(
          ticket => ticket === ticketId,
        );

      let newVoucherStatus = state.voucher.status;
      let newVoucherError = state.voucher.voucherError;

      // calculate the number of voucher uses in the current selected items
      if (ticketIsDiscountable && state.voucher.voucherCode.id) {
        let sum = 0;
        for (let i = 0; i < state.selectedItems.length; i++) {
          const selectedItem = state.selectedItems[i];
          if (
            isTicket(selectedItem) &&
            state.voucher.voucherApplicableTickets.findIndex(
              ticketId => ticketId === selectedItem.ticketId,
            ) >= 0
          ) {
            sum += selectedItem.quantity;
          }
        }

        // if the ticket was not selected before, its quantity ( which equals 1 )
        // should be added to the sum
        if (index === -1) {
          sum += 1;
        }

        const remainingUses =
          state.voucher.voucherCode.uses === 0
            ? config.maximumVoucherCodeUses
            : state.voucher.voucherCode.uses -
              state.voucher.voucherCode.claimed -
              sum;

        // needs to be re-written
        if (remainingUses < 0 && state.voucher.voucherCode.id) {
          newVoucherStatus = 'invalid';
          newVoucherError =
            'This code cannot be applied to the number of tickets you selected.';
        } else if (
          sum <= state.voucher.voucherCode.uses &&
          state.voucher.status === 'invalid'
        ) {
          newVoucherStatus = 'valid';
          newVoucherError = null;
        }
      }

      state.voucher.status = newVoucherStatus;
      state.voucher.voucherError = newVoucherError;

      if (index === -1) {
        const tax = getTaxPrice(
          ticketPriceData?.price,
          state.selectedEvent.tax_rate.rate,
        );
        const fee = getFeePrice(
          ticketPriceData?.price,
          state.selectedEvent.fees,
        );
        const feeTax = getTaxPrice(fee, state.selectedEvent.fee_tax_rate.rate);

        state.selectedItems.push({
          type: SelectedItemType.TICKET,
          ticketId: state.selectedEvent.tickets[itemIndex].id,
          idOrderItem: undefined,
          quantity: quantity,
          price: ticketPriceData?.price,
          tax,
          fee,
          feeTax,
          absorbFees: ticketPriceData.absorb_fees,
          name: state.selectedEvent.tickets[itemIndex].name,
          seatId,
          seatLabel,
        });
      }
    },
    addSeatSubtypesToCart(
      state,
      action: PayloadAction<AddSeatToCartPayload & { subtypeId: string }>,
    ) {
      const { ticketId, quantity, seatId, seatLabel, subtypeId } =
        action.payload;

      // this covers the case when a seat is deselected
      if (!subtypeId) {
        state.selectedItems = state.selectedItems.filter(
          item => isSeat(item) && item.seatId !== seatId,
        );
        return;
      }

      // find selected ticket inside the tickets of the current event
      const itemIndex = findIndex(state.selectedEvent.tickets, {
        id: ticketId,
      });

      const ticketPrice = state.selectedEvent.tickets[itemIndex].subtypes!.find(
        subtype => subtype.id === subtypeId,
      )!.price;

      // find the ticket inside the selected items
      const index = findIndex(state.selectedItems, {
        type: SelectedItemType.TICKET,
        ticketId: ticketId,
        seatId: seatId,
        seatLabel: seatLabel,
        subtypeId: subtypeId,
      });

      // there is an edge case that I don't understand yet, but we need a quick fix
      if (quantity === 0 && index === -1) {
        return;
      }

      // true if ticket was selected before
      if (index !== -1) {
        if (quantity === 0) {
          // remove the selected seat
          state.selectedItems = state.selectedItems.filter(
            item => isSeat(item) && item.subtypeId !== subtypeId,
          );
        } else {
          const tax = getTaxPrice(
            ticketPrice,
            state.selectedEvent.tax_rate.rate,
          );
          const fee = getFeePrice(ticketPrice, state.selectedEvent.fees);
          const feeTax = getTaxPrice(
            fee,
            state.selectedEvent.fee_tax_rate.rate,
          );
          state.selectedItems[index].quantity = quantity; // update the new quantity
          state.selectedItems[index].price = ticketPrice; // update the new price
          state.selectedItems[index].tax = tax; // update the new tax
          state.selectedItems[index].fee = fee; // update the new fee
          state.selectedItems[index].feeTax = feeTax; // update the new fee tax
        }
      }

      // check if ticket is discountable
      const ticketIsDiscountable =
        !!state.voucher.voucherApplicableTickets.find(
          ticket => ticket === ticketId,
        );

      let newVoucherStatus = state.voucher.status;
      let newVoucherError = state.voucher.voucherError;

      // calculate the number of voucher uses in the current selected items
      if (ticketIsDiscountable && state.voucher.voucherCode.id) {
        let sum = 0;
        for (let i = 0; i < state.selectedItems.length; i++) {
          const selectedItem = state.selectedItems[i];
          if (
            isTicket(selectedItem) &&
            state.voucher.voucherApplicableTickets.findIndex(
              ticketId => ticketId === selectedItem.ticketId,
            ) >= 0
          ) {
            sum += selectedItem.quantity;
          }
        }

        // if the ticket was not selected before, its quantity ( which equals 1 )
        // should be added to the sum
        if (index === -1) {
          sum += 1;
        }

        const remainingUses =
          state.voucher.voucherCode.uses === 0
            ? config.maximumVoucherCodeUses
            : state.voucher.voucherCode.uses -
              state.voucher.voucherCode.claimed -
              sum;

        // needs to be re-written
        if (remainingUses < 0 && state.voucher.voucherCode.id) {
          newVoucherStatus = 'invalid';
          newVoucherError =
            'This code cannot be applied to the number of tickets you selected.';
        } else if (
          sum <= state.voucher.voucherCode.uses &&
          state.voucher.status === 'invalid'
        ) {
          newVoucherStatus = 'valid';
          newVoucherError = null;
        }
      }

      state.voucher.status = newVoucherStatus;
      state.voucher.voucherError = newVoucherError;

      if (index === -1) {
        const tax = getTaxPrice(ticketPrice, state.selectedEvent.tax_rate.rate);
        const fee = getFeePrice(ticketPrice, state.selectedEvent.fees);
        const feeTax = getTaxPrice(fee, state.selectedEvent.fee_tax_rate.rate);
        const subtype = state.selectedEvent.tickets[itemIndex].subtypes!.find(
          subtype => subtype.id === subtypeId,
        )!;

        state.selectedItems.push({
          type: SelectedItemType.TICKET,
          ticketId: state.selectedEvent.tickets[itemIndex].id,
          idOrderItem: undefined,
          quantity: quantity,
          price: ticketPrice,
          tax,
          fee,
          feeTax,
          absorbFees:
            state.selectedEvent.tickets[itemIndex].ticketprice[0].absorb_fees,
          name: `${state.selectedEvent.tickets[itemIndex].name} (${subtype.name})`,
          seatId,
          seatLabel,
          subtypeId,
        });
      }
    },
    addCalendarTicketsToCart(
      state,
      action: PayloadAction<AddCalendarTicketToCartPayload>,
    ) {
      const { ticketId, quantity, date } = action.payload;

      // find selected ticket inside the tickets of the current event
      const itemIndex = findIndex(state.selectedEvent.tickets, {
        id: ticketId,
      });
      const ticketPriceData = getTicketPriceByQuantity(
        state.selectedEvent.tickets[itemIndex].ticketprice,
        quantity,
      );

      // find the ticket inside the selected items
      const index = findIndex(state.selectedItems, {
        type: SelectedItemType.TICKET,
        ticketId: ticketId,
        date: date,
      });

      // true if ticket was selected before
      if (index !== -1) {
        if (quantity === 0) {
          // remove the selected ticket along with its addons & top-ups
          state.selectedItems = state.selectedItems.filter(item =>
            isBundle(item)
              ? true
              : (isTicket(item) || isAddon(item) || isTopup(item)) &&
                (item.ticketId !== ticketId || item.date !== date),
          );
        } else {
          const tax = getTaxPrice(
            ticketPriceData?.price,
            state.selectedEvent.tax_rate.rate,
          );
          const fee = getFeePrice(
            ticketPriceData?.price,
            state.selectedEvent.fees,
          );
          const feeTax = getTaxPrice(
            fee,
            state.selectedEvent.fee_tax_rate.rate,
          );
          state.selectedItems[index].quantity = quantity; // update the new quantity
          state.selectedItems[index].price = ticketPriceData?.price; // update the new price
          state.selectedItems[index].tax = tax; // update the new tax
          state.selectedItems[index].fee = fee; // update the new fee
          state.selectedItems[index].feeTax = feeTax; // update the new fee tax
        }
      }

      // check if ticket is discountable
      const ticketIsDiscountable =
        !!state.voucher.voucherApplicableTickets.find(
          ticket => ticket === ticketId,
        );

      let newVoucherStatus = state.voucher.status;
      let newVoucherError = state.voucher.voucherError;

      // calculate the number of voucher uses in the current selected items
      if (ticketIsDiscountable && state.voucher.voucherCode.id) {
        let sum = 0;
        for (let i = 0; i < state.selectedItems.length; i++) {
          const selectedItem = state.selectedItems[i];
          if (
            isTicket(selectedItem) &&
            state.voucher.voucherApplicableTickets.findIndex(
              ticketId => ticketId === selectedItem.ticketId,
            ) >= 0
          ) {
            sum += selectedItem.quantity;
          }
        }

        // if the ticket was not selected before, its quantity ( which equals 1 )
        // should be added to the sum
        if (index === -1) {
          sum += 1;
        }

        const remainingUses =
          state.voucher.voucherCode.uses === 0
            ? config.maximumVoucherCodeUses
            : state.voucher.voucherCode.uses -
              state.voucher.voucherCode.claimed -
              sum;

        // needs to be re-written
        if (remainingUses < 0 && state.voucher.voucherCode.id) {
          newVoucherStatus = 'invalid';
          newVoucherError =
            'This code cannot be applied to the number of tickets you selected.';
        } else if (
          sum <= state.voucher.voucherCode.uses &&
          state.voucher.status === 'invalid'
        ) {
          newVoucherStatus = 'valid';
          newVoucherError = null;
        }
      }

      state.voucher.status = newVoucherStatus;
      state.voucher.voucherError = newVoucherError;

      if (index === -1) {
        const tax = getTaxPrice(
          ticketPriceData?.price,
          state.selectedEvent.tax_rate.rate,
        );
        const fee = getFeePrice(
          ticketPriceData?.price,
          state.selectedEvent.fees,
        );
        const feeTax = getTaxPrice(fee, state.selectedEvent.fee_tax_rate.rate);

        state.selectedItems.push({
          type: SelectedItemType.TICKET,
          ticketId: state.selectedEvent.tickets[itemIndex].id,
          idOrderItem: undefined,
          quantity: action.payload.quantity,
          price: ticketPriceData?.price,
          tax,
          fee,
          feeTax,
          absorbFees: ticketPriceData.absorb_fees,
          name: state.selectedEvent.tickets[itemIndex].name,
          date: action.payload.date,
        });
      }
    },
    addTicketTypeBundlesToCart(
      state,
      action: PayloadAction<AddTicketTypeBundlesToCartPayload>,
    ) {
      if (!state.selectedEvent.ticketTypeBundles) return;

      const { id, date } = action.payload;

      const index = findIndex(state.selectedItems, {
        type: SelectedItemType.BUNDLE,
        id,
        date,
      });

      if (index !== -1) {
        state.selectedItems[index].quantity++;
      } else {
        const { name, price, absorbFees, items } =
          state.selectedEvent.ticketTypeBundles.find(
            bundle => bundle.id === id,
          )!;
        const ticketTypes = getTicketTypes(items, state.selectedEvent.tickets);
        const tax = getTaxPrice(price, state.selectedEvent.tax_rate.rate);
        const fee = getBundleFeePrice(
          state.selectedEvent.fees,
          ticketTypes,
          items,
          price,
        );
        const feeTax = getTaxPrice(fee, state.selectedEvent.fee_tax_rate.rate);

        state.selectedItems.push({
          type: SelectedItemType.BUNDLE,
          date,
          id,
          name,
          quantity: 1,
          price,
          tax,
          fee,
          feeTax,
          absorbFees,
          orderItems: [],
        });
      }
    },
    removeTicketTypeBundlesFromCart(
      state,
      action: PayloadAction<AddTicketTypeBundlesToCartPayload>,
    ) {
      if (!state.selectedEvent.ticketTypeBundles) return;

      const { id, date } = action.payload;

      const index = findIndex(state.selectedItems, {
        type: SelectedItemType.BUNDLE,
        id,
        date,
      });

      if (state.selectedItems[index].quantity === 1) {
        state.selectedItems.splice(index, 1);
      } else {
        state.selectedItems[index].quantity--;
      }
    },
    addAddonsToSelectedItems(
      state,
      action: PayloadAction<AddAddonsToSelectedItemsPayload>,
    ) {
      const {
        event,
        addon: {
          ticketId,
          addonId,
          quantity,
          ticketIndex,
          ticketDate,
          absorbFees,
        },
      } = action.payload;
      if (state.selectedEvent.id !== event.id) {
        state.selectedEvent = event;
      }

      // find the ticket index within selected event
      const index = findIndex(state.selectedEvent.tickets, {
        id: ticketId,
      });
      const addonIndex = findIndex(
        state.selectedEvent.tickets[index].ticketaddons,
        {
          id: addonId,
        },
      );
      const addonIsSelected = findIndex(state.selectedItems, {
        type: SelectedItemType.ADDON,
        addonId,
        ticketIndex,
      });

      if (addonIsSelected !== -1) {
        if (
          quantity === -1 &&
          state.selectedItems[addonIsSelected].quantity === 1
        ) {
          state.selectedItems.splice(addonIsSelected, 1);
        } else {
          state.selectedItems[addonIsSelected].quantity += quantity;
        }
        return state;
      } else {
        const { price, name } =
          state.selectedEvent.tickets[index].ticketaddons[addonIndex];
        const tax = getTaxPrice(price, state.selectedEvent.tax_rate.rate);
        const fee = getFeePrice(price, state.selectedEvent.fees);
        const feeTax = getTaxPrice(fee, state.selectedEvent.fee_tax_rate.rate);
        state.selectedItems.push({
          type: SelectedItemType.ADDON,
          ticketId,
          addonId,
          ticketIndex,
          quantity,
          price,
          tax,
          fee,
          feeTax,
          absorbFees,
          name,
          date: ticketDate,
        });
      }
    },
    removeTopUpsAndUnbundledFromSelectedItems(state) {
      state.selectedItems = state.selectedItems.filter(
        item => isTopup(item) === false && isAddon(item) === false,
      );
    },
    addTopUpsToSelectedItems(
      state,
      action: PayloadAction<{
        ticketId: string;
        ticketIndex: number;
        price: number;
        idOrderItem: string;
        ticketDate: string | undefined;
      }>,
    ) {
      const { ticketId, ticketIndex, price, idOrderItem, ticketDate } =
        action.payload;
      // find top-up inside the selected items
      const topUpIndex = findIndex(state.selectedItems, {
        type: SelectedItemType.TOPUP,
        ticketIndex,
        ticketId,
      });

      // if it exists then update it
      if (topUpIndex !== -1) {
        if (price) {
          state.selectedItems[topUpIndex].price = price;
        } else {
          state.selectedItems.splice(topUpIndex, 1);
        }
      } else {
        state.selectedItems.push({
          type: SelectedItemType.TOPUP,
          ticketId,
          ticketIndex,
          idOrderItem,
          quantity: 1,
          name: 'Top-up',
          price,
          tax: 0,
          fee: 0,
          feeTax: 0,
          absorbFees: true,
          date: ticketDate,
        });
      }
    },
  },
  extraReducers: {
    [fetchEventWithTickets.pending]: state => {
      state.loading = true;
    },
    [fetchEventWithTickets.fulfilled]: (state, { payload }) => {
      if (payload && payload.tickets) {
        payload.tickets.map(item => {
          item.quantity = 0;
          item.ticketaddons.map(addon => {
            addon.quantity = 0;

            return addon;
          });

          return item;
        });
      }
      state.loading = false;

      // set event
      if (state.selectedEvent.id === payload.id) {
        const { status, voucherCode } = state.voucher;
        const privateInvitationHasBeenApplied =
          status === 'valid' && !voucherCode.id;

        if (privateInvitationHasBeenApplied) {
          state.selectedEvent = {
            ...payload,
            tickets: state.selectedEvent.tickets,
          };
        }
      }

      // set fees & taxes
      const feesAndTaxes = {
        ...payload.fees_and_taxes,
      };

      delete payload.fees_and_taxes;

      state.selectedEvent = {
        ...payload,
        ...feesAndTaxes,
      };

      // remove selected items from other events
      state.selectedItems = state.selectedItems.filter(item => {
        if (isTicket(item)) {
          return (
            state.selectedEvent.tickets.findIndex(
              ticket => ticket.id === item.ticketId,
            ) >= 0
          );
        }
        if (isBundle(item) && state.selectedEvent.ticketTypeBundles) {
          return (
            state.selectedEvent.ticketTypeBundles.findIndex(
              bundle => bundle.id === item.id,
            ) >= 0
          );
        }
        return false;
      });
    },
    [fetchEventWithTickets.rejected]: () => initialState,
    [fetchVoucherData.pending]: state => {
      state.voucher.loading = true;
    },
    [fetchVoucherData.fulfilled]: (state, { payload }) => {
      state.voucher.loading = false;
      let voucherStatus: VoucherStatus = 'invalid';

      // handle private tickets
      const privateTickets = payload.private_tickets;
      const currentTickets = state.selectedEvent.tickets;

      if (privateTickets.length > 0) {
        const privateTicketsToBeAdded = privateTickets.filter(
          ticket => currentTickets.findIndex(item => item.id === ticket.id) < 0,
        );
        state.selectedEvent.tickets.unshift(...privateTicketsToBeAdded);
        state.voucher.status = 'valid';
      }

      if (
        privateTickets.length === 0 &&
        payload.voucher_applicable_tickets.length === 0
      ) {
        const initialVoucherState = () => ({
          ...initialVoucher,
          status: 'invalid',
        });
        state.voucher = initialVoucherState() as VoucherInterface;
        return;
      }

      // select vouchers data
      const voucherData: VoucherData = payload.voucher.data;
      const voucherCodeData: VoucherCodeData = payload.voucher_code.data;
      const voucherApplicableTickets: VoucherApplicableTickets =
        payload.voucher_applicable_tickets;
      const voucherError = payload.voucher_error;

      let sum = 0;

      for (let i = 0; i < state.selectedItems.length; i++) {
        const selectedItem = state.selectedItems[i];
        if (
          isTicket(selectedItem) &&
          voucherApplicableTickets.findIndex(
            ticket => ticket === selectedItem.ticketId,
          ) >= 0
        ) {
          sum += selectedItem.quantity;
        }
      }

      const remainingUses =
        voucherCodeData.uses === 0
          ? config.maximumVoucherCodeUses
          : voucherCodeData.uses - voucherCodeData.claimed - sum;

      // check if it is private invitation
      if (privateTickets.length > 0) {
        voucherStatus = 'valid';
      } else if (remainingUses < 0) {
        voucherStatus = 'invalid';
        state.voucher.voucherError =
          'This code cannot be applied to the number of tickets you selected.';
        state.voucher.voucherData = voucherData;
        state.voucher.voucherCode = voucherCodeData;
        state.voucher.voucherApplicableTickets = voucherApplicableTickets;
      } else if (
        // check if it is valid
        voucherError === null &&
        remainingUses >= 0 &&
        voucherApplicableTickets.length > 0
      ) {
        voucherStatus = 'valid';
      } else if (voucherError || voucherApplicableTickets.length === 0) {
        voucherStatus = 'invalid';
        state.voucher.voucherData = initialVoucherData;
        state.voucher.voucherCode = initialVoucherCodeData;
        state.voucher.voucherApplicableTickets = [];
        state.voucher.voucherError = voucherError;
      }

      if (voucherStatus === 'valid') {
        // insert voucher data to the state
        state.voucher.voucherData = voucherData;
        state.voucher.voucherCode = voucherCodeData;
        if (privateTickets.length > 0) {
          state.voucher.voucherCode.code = payload.voucherCode;
        }
        state.voucher.voucherApplicableTickets = voucherApplicableTickets;
        state.voucher.voucherError = voucherError;
      }

      state.voucher.status = voucherStatus;
    },
    [fetchVoucherData.rejected]: state => {
      state.voucher.loading = false;
      state.voucher.status = 'invalid';
    },
    [fetchCartData.pending]: state => {
      state.loading = true;
    },
    [fetchCartData.fulfilled]: (
      state,
      action: PayloadAction<FetchCartDataPayload>,
    ) => {
      state.loading = false;

      const {
        serverResponse: { tickets: orderedTickets },
        tickets,
        bundles,
        selectedEvent,
      } = action.payload;

      // set tickets & bundles
      const formattedTickets: CartTicket[] = tickets.map(ticket => ({
        ...ticket,
        idOrderItem: getIdOrderItemForTicket(
          ticket,
          selectedEvent,
          orderedTickets,
        ),
      }));

      const formattedBundles: CartBundle[] = bundles.map(bundle => ({
        ...bundle,
        orderItems: getOrderItemsForBundle(
          bundle,
          selectedEvent,
          orderedTickets,
        ),
      }));

      state.selectedItems = [...formattedTickets, ...formattedBundles];
    },
    [fetchCartData.rejected]: state => {
      state.loading = false;
    },
  },
});

export const {
  actions: {
    setAllTheSelectedItems,
    setQueryParams,
    removeQueryParams,
    removeAllTheSelectedItems,
    addAddonsToSelectedItems,
    removeTopUpsAndUnbundledFromSelectedItems,
    addTopUpsToSelectedItems,
    addTicketsToCart,
    addSeatsToCart,
    addSeatSubtypesToCart,
    addCalendarTicketsToCart,
    addTicketTypeBundlesToCart,
    removeTicketTypeBundlesFromCart,
    resetVoucherData,
    updateVoucherStatus,
  },
} = slice;

export default slice.reducer;
